import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { POLICIES, POLICY_INDEX } from "../content/policies";

/**
 * Shared layout for every public legal page.
 * Props:
 *   slug — one of the keys in POLICIES (e.g. "terms", "privacy").
 *   Route-level pages pass slug explicitly; the parametric route reads it from useParams.
 */
export default function PolicyPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const doc = POLICIES[slug];

  useEffect(() => {
    if (doc) {
      window.scrollTo(0, 0);
    }
  }, [slug, doc]);

  const navSections = useMemo(() => (doc?.sections || []).map((s, i) => ({
    id: `sec-${i}`,
    label: s.heading,
  })), [doc]);

  if (!doc) {
    return (
      <div className="pol-shell" data-testid="policy-not-found">
        <div className="pol-main"><h1>Policy not found</h1><p><Link to="/">← Back home</Link></p></div>
      </div>
    );
  }

  return (
    <div className="pol-shell" data-testid={`policy-${slug}`}>
      <header className="pol-nav" data-testid="policy-nav">
        <Link to="/" className="pol-brand" data-testid="policy-home-link">
          <img src="/logo.png" alt="GoPrintPack" />
        </Link>
        <nav className="pol-topnav">
          <Link to="/" data-testid="policy-nav-home">Home</Link>
          <Link to="/contact" data-testid="policy-nav-contact">Contact</Link>
          <Link to="/login" data-testid="policy-nav-login">Login</Link>
        </nav>
      </header>

      <div className="pol-body">
        <aside className="pol-side" data-testid="policy-sidebar">
          <div className="pol-side-group">
            <div className="pol-side-head">All policies</div>
            {POLICY_INDEX.map(p => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`policy-link-${p.slug}`}
                className={`pol-side-item ${p.slug === slug ? "active" : ""}`}
              >
                {p.title}
              </Link>
            ))}
          </div>
          {navSections.length > 0 && (
            <div className="pol-side-group">
              <div className="pol-side-head">On this page</div>
              {navSections.map(s => (
                <a key={s.id} href={`#${s.id}`} className="pol-side-sub" data-testid={`policy-toc-${s.id}`}>
                  {s.label}
                </a>
              ))}
            </div>
          )}
        </aside>

        <main className="pol-main" data-testid="policy-content">
          <div className="pol-meta">
            <span className="pol-chip">Legal · Version {doc.version}</span>
            <span className="pol-meta-sep">·</span>
            <span>Effective {doc.lastUpdated}</span>
          </div>
          <h1 data-testid="policy-title">{doc.title}</h1>
          <p className="pol-entity" dangerouslySetInnerHTML={{ __html: doc.entityLine }} />
          {doc.intro && <p className="pol-intro" dangerouslySetInnerHTML={{ __html: doc.intro }} />}

          {doc.sections.map((s, i) => (
            <section key={i} id={`sec-${i}`} className="pol-section" data-testid={`policy-section-${i}`}>
              <h2>{s.heading}</h2>
              {s.blocks.map((b, j) => <Block key={j} block={b} />)}
            </section>
          ))}

          <footer className="pol-foot">
            <p>
              For any question on this policy, write to{" "}
              <a href="mailto:info@goprintpack.com" data-testid="policy-contact-email">info@goprintpack.com</a>.
            </p>
            <p className="pol-foot-small">
              © Tejas Industries. All rights reserved. GoPrintPack™ is a trademark of Tejas Industries.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}

function Block({ block }) {
  if (block.type === "p") {
    return <p className="pol-p" dangerouslySetInnerHTML={{ __html: block.html }} />;
  }
  if (block.type === "list") {
    return (
      <ul className="pol-list">
        {block.items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} />)}
      </ul>
    );
  }
  if (block.type === "orderedList") {
    return (
      <ol className="pol-olist">
        {block.items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} />)}
      </ol>
    );
  }
  if (block.type === "table") {
    return (
      <div className="pol-table-wrap">
        <table className="pol-table">
          <thead>
            <tr>{block.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => <td key={j} dangerouslySetInnerHTML={{ __html: cell }} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === "callout") {
    return (
      <div className={`pol-callout pol-callout-${block.tone || "info"}`}>
        <span dangerouslySetInnerHTML={{ __html: block.html }} />
      </div>
    );
  }
  return null;
}
